import type { BadgeColor } from '$lib/components/atoms/badge/types';
import type { ChipScheme } from '$lib/components/atoms/chip/types';
import type { IconName } from '$lib/components/icons/types';
import type { PoiCategoryTrackerName } from '$lib/services/tracking/googleAnalytics/events';
import type { PoiFilterTrackerName } from '$lib/services/tracking/googleAnalytics/type';
import type {
	AccommodationSubtype,
	Connector,
	DatasheetPoiType,
	PartnerPoiCategory,
	PoiCategory,
	PoiFilters,
	PoiScheme,
	PoiType,
} from './types';
import * as m from '$paraglide/messages';

// Bitmask for poi categories
export const poiCategoryBits: Record<PoiCategory, number> = {
	ACCOMMODATION: 1,
	RESTAURANT: 2,
	TOURISM: 4,
	SERVICE_GAS_STATION: 8,
	EV_SERVICE: 16,
	PARKING: 32,
	REST_STOP: 64,
	PARTNER: 128,
	PARTNER2: 256,
};

export const poiCategoryTrackerNames: Record<PoiCategory, PoiCategoryTrackerName> = {
	RESTAURANT: 'restaurant',
	ACCOMMODATION: 'accommodation',
	TOURISM: 'tourism',
	SERVICE_GAS_STATION: 'gasStation',
	EV_SERVICE: 'ev',
	PARKING: 'parking',
	REST_STOP: 'restStop',
	PARTNER: 'partner_engie',
	PARTNER2: 'partner_mobilize',
};

export const poiTypeTrackerNames: Record<PoiType, PoiCategoryTrackerName> = {
	AccommodationPOI: 'accommodation',
	AccommodationMichelinPOI: 'accommodation',
	RestaurantPOI: 'restaurant',
	RestaurantMichelinPOI: 'restaurant',
	TourismPOI: 'tourism',
	ServiceEVPOI: 'ev',
	ParkingPOI: 'parking',
	ServiceGasStationPOI: 'gasStation',
	ServicePOI: 'gasStation',
	ServiceRestStopPOI: 'restStop',
	PartnerPOI: 'partner_engie',
	Partner2POI: 'partner_mobilize',
};

export const poiFilterTrackerNames: Record<
	PoiAccommodationFilters,
	PoiFilterTrackerName
> = {
	ACCOMMODATION_RATE: 'rate',
	ACCOMMODATION_STARS: 'poi_distinction',
	ACCOMMODATION_PRICE: 'price_range', // deprecated
	ACCOMMODATION_MAX_PRICE: 'max_price',
	ACCOMMODATION_TYPE: 'types',
	ACCOMMODATION_EQUIPMENT: 'equipments',
	MICHELIN_ACCOMMODATION: 'michelin_selection',
	ACCOMMODATION_AVAILABILITY_DATE_RANGE: 'checkin_checkout_date',
	BOOKABLE_PARKING: 'reservation_activated',
	FAMILY_FRIENDLY_REST_STOP: 'family_friendly_activated',
	CONNECTOR: 'charging_spots_details',
	GAS_STATION_EQUIPMENT: 'equipments',
	GAS_STATION_LAST_UPDATE: 'last_update',
	GAS_STATION_NETWORK: 'network',
	GAS_STATION_TYPE: 'types',
	INCLUDE_GREEN_STAR_RESTAURANT: 'green_star',
	MICHELIN_RESTAURANT: 'poi_distinction',
	OTHER_RESTAURANT_RATE: 'rate',
	POWER: 'charging_station_power',
	RESTAURANT_MAX_PRICE: 'max_price',
	TOURISM_MICHELIN_DISTINCTION: 'poi_distinction',
	ONLY_MICHELIN_GUIDE_SELECTION: 'michelin_selection',
	TOURISM_TYPE_PLACES: 'type_places',
	MICHELIN_ACCOMMODATION_DISTINCTION: 'poi_accomodation_key',
};

type PoiAccommodationFilters = PoiFilters['ACCOMMODATION'][number]['id'];

export const poiCategoryChipSchemes: Record<
	Exclude<PoiCategory, PartnerPoiCategory>,
	ChipScheme
> = {
	RESTAURANT: 'restaurant',
	ACCOMMODATION: 'hotel',
	TOURISM: 'tourism',
	SERVICE_GAS_STATION: 'energy',
	EV_SERVICE: 'electric',
	PARKING: 'parking',
	REST_STOP: 'rest',
};

export const poiCategoryBadgeSchemes: Record<
	Exclude<PoiCategory, PartnerPoiCategory>,
	BadgeColor
> = {
	RESTAURANT: 'restaurant',
	ACCOMMODATION: 'hotel',
	TOURISM: 'tourism',
	SERVICE_GAS_STATION: 'energy',
	EV_SERVICE: 'electric',
	PARKING: 'parking',
	REST_STOP: 'rest',
};

export const poiCategoryIcons: Record<Exclude<PoiCategory, PartnerPoiCategory>, IconName> = {
	RESTAURANT: 'restaurant',
	ACCOMMODATION: 'hotel',
	TOURISM: 'tourism',
	SERVICE_GAS_STATION: 'energy-pump',
	EV_SERVICE: 'flash',
	PARKING: 'parking',
	REST_STOP: 'rest-stop',
};

export const connectorTypeToIconNameMap: Record<Connector['type'], IconName> = {
	CCS1: '',
	SCAME: 'connector-scame',
	CCS2: 'connector-ccs2',
	CHADEMO: 'connector-chademo',
	MENNEKES: 'connector-mennekes',
	STANDARD_HOUSEHOLD: 'connector-standard-household',
	TESLA: 'connector-tesla',
};

export const MAX_PRICE_CATEGORY = 4;
export const DIVIDER_FOR_W_TO_KW = 1000;

export const poiScheme: Record<Exclude<DatasheetPoiType, 'PartnerPOI' | 'Partner2POI'>, PoiScheme> = {
	AccommodationPOI: 'hotel',
	AccommodationMichelinPOI: 'hotel',
	RestaurantPOI: 'restaurant',
	RestaurantMichelinPOI: 'restaurant',
	TourismPOI: 'tourism',
	ServiceEVPOI: 'electric',
	ServiceRestStopPOI: 'rest',
};

export const MAX_SELECTION_DAYS = 90;

export function getAccommodationSubTypeNames(): Record<AccommodationSubtype, string> {
	return {
		BB: m.poi_datasheet__highlight__accommodation_subtype__bb(),
		CAMPING: m.poi_datasheet__highlight__accommodation_subtype__camping(),
		HOTEL: m.poi_datasheet__highlight__accommodation_subtype__hotel(),
		RESIDENCE: m.poi_datasheet__highlight__accommodation_subtype__residence(),
	};
}

export const POI_FILTER_ACCOMMODATION_RATE_DEFAULT_VALUE = 7;
